/**
 * The design's three-way payment-method picker (COL-533).
 *
 * V2 used to show the current method and a "Change" link back to V1. The
 * export puts the choice inline: three cards — SEPA, bank transfer, card —
 * with the selected one outlined, a detail strip underneath, and Save.
 *
 * What this does and does not do, deliberately:
 *
 * It writes `bookings.preferred_payment_method`, which is exactly what V1's
 * `choosePayMethod` writes, and it defers the decision of WHETHER that write
 * is allowed to `resolvePaymentMethodChoice` — shared with V1. The rule that
 * matters: a tenant cannot switch away from an active SEPA mandate, because
 * ending a standing authorisation to debit their account is admin-approved.
 * A picker that quietly wrote the new method would switch off a direct debit
 * we still believe is live.
 *
 * It does NOT grant a mandate or take a payment. `SepaMandateForm` is a full
 * §19 SEPA form and the card checkout is a Stripe flow; both still run on V1,
 * per Ganesh's "reuse what exists… restyle only, no new payment logic".
 */

import { useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import {
  PaymentMethodChoice,
  resolvePaymentMethodChoice,
} from "@/lib/payment-method-choice";

type Labels = Record<string, string>;

type Props = {
  bookingId: number | null;
  /** The method currently stored on the booking. */
  current: PaymentMethodChoice | null;
  /** Masked IBAN of the active mandate, when there is one. */
  last4: string | null;
  mandateActive: boolean;
  /** SEPA is a rail for THIS booking (resolver-backed, not a stored mandate). */
  sepaAvailable: boolean;
  cardAvailable: boolean;
  transferAvailable: boolean;
  /** Day of the month rent is due — never assume the 1st. */
  dueDay: number | null;
  /** True while the resolver/mandate state is still unknown. */
  stateUnknown: boolean;
  /**
   * Called after the new method is actually stored — the section folds itself
   * back up (Neel, 23 Oct). Only on success: leaving the picker open after a
   * failed write is what lets the tenant see the error and try again.
   */
  onSaved?: () => void;
  d: Labels;
};

export default function PaymentMethodPicker({
  bookingId,
  current,
  last4,
  mandateActive,
  sepaAvailable,
  cardAvailable,
  transferAvailable,
  dueDay,
  stateUnknown,
  onSaved,
  d,
}: Props) {
  const queryClient = useQueryClient();
  /*
    The RAW selection. It is clamped to an offered rail below, once `options`
    is known -- `current` is whatever is stored on the booking, which may be
    a method this room does not offer (an admin changed the rails, or the
    booking moved to an owner-collected room). Seeding state from it
    unchecked started the picker on a card option that is not rendered: the
    detail strip described card payment, nothing looked selected, and the
    only way out was to notice and pick again.
  */
  const [rawChoice, setRawChoice] = useState<PaymentMethodChoice>(
    current ?? (mandateActive ? "sepa_debit" : "bank_transfer")
  );
  /*
    Clamping can move `choice` off `current` before the tenant touches
    anything -- which would arm Save on mount and let a stray click write a
    method they never chose. The write needs an explicit pick.
  */
  const [touched, setTouched] = useState(false);
  const day = String(dueDay ?? 1);
  const withDay = (text: string) => text.replace(/\{day\}/g, day);
  const [saving, setSaving] = useState(false);

  /*
    Only the rails this booking actually has.

    `get_room_payment_methods` resolves them and the charge functions ask the
    same resolver, so a method offered here is one that can really be
    charged. Bank transfer is always present: it is the only rail that works
    under owner_collects, and the resolver forces it on there.
  */
  const options: Array<{
    key: PaymentMethodChoice;
    title: string;
    blurb: string;
    recommended?: boolean;
  }> = [
    ...(sepaAvailable
      ? [
          {
            key: "sepa_debit" as const,
            title: d.methodSepaTitle,
            blurb: withDay(d.methodSepaBlurb),
            recommended: true,
          },
        ]
      : []),
    ...(transferAvailable
      ? [
          {
            key: "bank_transfer" as const,
            title: d.methodTransferTitle,
            blurb: withDay(d.methodTransferBlurb),
          },
        ]
      : []),
    ...(cardAvailable
      ? [
          {
            key: "card" as const,
            title: d.methodCardTitle,
            blurb: withDay(d.methodCardBlurb),
          },
        ]
      : []),
  ];

  /*
    Clamp to something actually offered.

    Derived rather than corrected in an effect: `options` depends on props
    that arrive asynchronously, so a one-shot fix-up would run against an
    empty list on the first render and then never again. SEPA is preferred
    when a mandate is live -- that is the rail actually collecting -- then
    the stored method, then whatever the room does offer.
  */
  const choice: PaymentMethodChoice =
    options.find((o) => o.key === rawChoice)?.key ??
    (mandateActive && options.some((o) => o.key === "sepa_debit")
      ? "sepa_debit"
      : (options[0]?.key ?? rawChoice));

  const detail = withDay(
    choice === "sepa_debit"
      ? /*
           A SEPA PREFERENCE is not a mandate. `rent-charge-tick` collects
           only against an active mandate and never reads
           `preferred_payment_method`, so promising collection here to a
           tenant without one describes a debit that will never happen —
           and they would stop paying.
         */
        mandateActive && last4
        ? d.methodSepaDetail.replace("{iban}", `•••• ${last4}`)
        : d.methodSepaDetailNone
      : choice === "bank_transfer"
        ? d.methodTransferDetail
        : d.methodCardDetail
  );

  const pick = (key: PaymentMethodChoice) => {
    const outcome = resolvePaymentMethodChoice(key, {
      hasActiveMandate: mandateActive,
      // V2 does not file cancellation requests; it points at the surface
      // that does, so it never reports one as already pending.
      cancelAlreadyRequested: false,
    });
    if (outcome.action !== "switch") {
      toast.info(d.methodMandateLocked);
      return;
    }
    setRawChoice(outcome.method);
    setTouched(true);
  };

  const save = async () => {
    if (bookingId == null || stateUnknown) return;
    /*
      Re-run the guard at WRITE time, not only at selection time.

      `mandateActive` can flip to true between opening the picker and
      pressing Save — granting a mandate in the dialog below does exactly
      that — and the selection made beforehand would otherwise be written
      unchecked. The debit itself keeps running either way (the collector
      reads the mandate, not this column), so the real harm is a tenant who
      believes they switched to transfer, pays again by hand, and is
      debited as well.
    */
    const guard = resolvePaymentMethodChoice(choice, {
      hasActiveMandate: mandateActive,
      cancelAlreadyRequested: false,
    });
    if (guard.action !== "switch") {
      toast.info(d.methodMandateLocked);
      return;
    }
    setSaving(true);
    try {
      const { error } = await (supabase as any)
        .from("bookings")
        .update({ preferred_payment_method: choice })
        .eq("id", bookingId);
      if (error) throw error;
      toast.success(d.methodSaved);
      queryClient.invalidateQueries({ queryKey: ["payment-methods"] });
      queryClient.invalidateQueries({ queryKey: ["active-booking"] });
      onSaved?.();
    } catch (e: any) {
      toast.error(e?.message || d.methodSaveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 border-t border-border/60 p-6">
      <div className="grid gap-3 sm:grid-cols-3">
        {options.map((o) => {
          const selected = choice === o.key;
          return (
            <button
              key={o.key}
              type="button"
              aria-pressed={selected}
              onClick={() => pick(o.key)}
              className={[
                "rounded-2xl border p-4 text-left transition-colors",
                selected
                  ? "border-foreground bg-muted"
                  : "border-border bg-card hover:bg-muted/50",
              ].join(" ")}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="font-semibold text-foreground">{o.title}</span>
                {o.recommended ? (
                  <span className="shrink-0 rounded-full bg-[hsl(174_52%_72%)] px-2.5 py-1 text-[12px] font-semibold text-foreground">
                    {d.methodSepaRecommended}
                  </span>
                ) : null}
              </span>
              <span className="mt-1 block text-[13.5px] text-muted-foreground">
                {o.blurb}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-muted px-5 py-4">
        <p className="min-w-0 text-sm text-foreground">{detail}</p>
        <Button
          className="shrink-0 rounded-full px-6"
          disabled={
            saving ||
            stateUnknown ||
            bookingId == null ||
            !touched ||
            choice === current
          }
          onClick={() => void save()}
        >
          {d.methodSave}
        </Button>
      </div>
    </div>
  );
}
